import { KeyboardEvent, ClipboardEvent, MouseEvent, useState, useEffect, useCallback } from 'react';
import { IServerValidation } from '../../../../../domain/validation/interfaces';
import ValidationErrors from '../../../../../domain/validation/server-errors/validation-errors';
import {
    createControl,
    updateControl,
    validateForm,
    removeInvisibleCharsOnPaste,
    validateControl
} from '../../../../../domain/validation/validation';
import { getSimpleCaptchaValidationError } from '../../../../../domain/captcha/simple-captcha';
import { getHiddenDataBoolean, getIsChina } from '../../../../../domain/hidden-data-provider';
import { getRecaptchaToken, renderRecaptcha2, resetRecaptcha2, getRecaptcha2Error } from '../../../../../domain/captcha/recaptcha-provider';
import Recaptcha3Text from '../../../../../domain/captcha/recaptcha3-terms';
import { initAdwords } from '../../../../../domain/marketing/adwords-provider';
import { gtmCreateAccount, sendGAWavesUserGuidEvent } from '../../../../../domain/marketing/gtm-provider';
import { impactRadiusCreateAccount } from '../../../../../domain/marketing/impact-radius-provider';
import { tiktokCreateAccount } from '../../../../../domain/marketing/tiktok-provider';
import { getUTMFromStorage } from '../../../../../domain/marketing/utm/utm-provider';
import { getPageNodeGUID } from '../../../../../domain/common';
import { IFormControl } from '../../../../../ui/forms/interfaces';
import FormTextField from '../../../../../ui/forms/fields/text-field';
import ChinaCaptchaField from '../../../../../ui/forms/fields/captcha-china-field';
import FormPreloader from '../../../../../ui/forms/form-preloader';
import { createAccount } from '../../../../membership/dal/data-provider';
import FormTerms from '../../../../../ui/forms/form-terms';
import { billingAddressActions, rootActions, useAppDispatch, useAppSelector } from '../../data/store';
import { Step2Tabs } from '../../enums/step-2-tabs';
import Tooltip from '../../../../../ui/tooltip/tooltip';
import { CartSteps } from '../../enums/cart-steps';
import { facebookLead } from '../../../../../domain/facebook/facebook-marketing-provider';
import { CreateAccountReferrer } from '../../../../membership/enums/create-account-referrer';
import { getGdprCookie } from '../../../../gdpr/gdpr-provider';
import {
    GOOGLE_ERROR,
    GOOGLE_SIGN_IN_INITIALIZED,
    GOOGLE_SIGNED_IN
} from '../../../../../domain/google/google-login-provider';
import ExternalLogin from '../../../../membership/login/external-login/external-login';
import { getLocalData } from '../../domain/shopping-cart-address-provider';

/**
 * Shopping Cart Create Account
 */
const CartCreateAccount = () => {

    const dispatch = useAppDispatch();
    const isChina = getIsChina();
    const shouldDisableCheckout = getHiddenDataBoolean('disable-checkout-setting');

    // validation ---------------------------------------
    const emailControl = createControl('email', '', {
        required: true,
        email: true,
    });

    const simpleCaptchaControl = createControl('simpleCaptcha', '', {
        required: true,
        requiredMessage: getSimpleCaptchaValidationError(),
        length: 5,
    });

    const [isLoading, setIsLoading] = useState(false);
    const [response, setResponse] = useState<IServerValidation|null>();
    const [email, setEmail] = useState<IFormControl>(emailControl);

    // this country / state data is used for tax calculations
    const updateCount = useAppSelector(store => store.billingAddress.updateCount);

    // simple captcha - used for chine
    const [simpleCaptchaCode, setSimpleCaptchaCode] = useState<IFormControl>(simpleCaptchaControl);
    const [simpleCaptchaAttempts, setSimpleCaptchaAttempts] = useState(0);

    // google recaptcha version 2 & 3
    const [recaptchaVersion, setRecaptchaVersion] = useState(3);
    const [recaptcha2WidgetID, setRecaptcha2WidgetID] = useState(0);

    // captcha: common
    const [captchaError, setCaptchaError] = useState('');

    useEffect(() => {
        document.body.dispatchEvent(new CustomEvent(GOOGLE_SIGN_IN_INITIALIZED));
    }, []);

    /**
     * if recaptcha 3 returns low score (the limit is defined in kentico settings),
     * then recaptcha 2 fallback should be rendered inside <div id="recaptcha"></div>
     */
    const renderCaptcha2Fallback = () => {
        if(!window.grecaptcha) return;

        window.grecaptcha.ready(async () => {
            const widgetID = await renderRecaptcha2('recaptcha');
            setRecaptcha2WidgetID(widgetID);
        });
    };

    /**
     * apply all marketing scripts & events
     */
    const initMarketing = async (email: string) => {

        const gdpr = getGdprCookie();
        const isPerformance = gdpr ? gdpr.IsPerformance : true;
        const isMarketing = gdpr ? gdpr.IsMarketing : true;

        // send data to GTM after registration
        gtmCreateAccount('from checkout');

        if(isMarketing){
            // init ad-words script
            initAdwords();

            // facebook lead
            facebookLead();

            // send tik-tok create account event
            tiktokCreateAccount();
        }

        if(isPerformance){
            // impact radius
            impactRadiusCreateAccount();
        }
    };

    const onCreateAccountSuccess = useCallback(async (response: { data: IServerValidation, statusCode: number }) => {
        // update cart data
        dispatch(
            rootActions.setCartData(response.data.relatedData)
        );

        // update the address controls from the signup response (reset them in this case)
        dispatch(
            billingAddressActions.main({
                updateCount: updateCount + 1,
            })
        );

        // handle marketing script & events
        await initMarketing(email.value);

        sendGAWavesUserGuidEvent(response.data.relatedData.gA_GUID);

        // navigate to the third step
        dispatch(
            rootActions.main({
                cartStep: CartSteps.Third,
            })
        );
    }, [dispatch, email.value, updateCount]);

    const onCreateAccountError = useCallback((response: { data: IServerValidation, statusCode: number } | null) => {
        if(isChina){

            // re-render simple captcha
            setSimpleCaptchaAttempts(simpleCaptchaAttempts + 1);
        }
        else{
            if(response?.data.relatedData === 'recaptcha-error') {
                setRecaptchaVersion(2);
                renderCaptcha2Fallback();
            }

            if(recaptchaVersion === 2){
                resetRecaptcha2();
            }
        }

        // render errors
        setResponse(response?.data);
    }, [isChina, recaptchaVersion, simpleCaptchaAttempts]);

    /**
     * perform user login on server
     */
    const performCreateAccount = async () => {

        // client side validation
        const isValid =
            isChina ?
                validateForm([
                    [email, setEmail],
                    [simpleCaptchaCode, setSimpleCaptchaCode],
                ]) :
                validateForm([
                    [email, setEmail],
                ]);

        if(!isValid) return;

        setIsLoading(true);

        let response = null;

        try {
            const captchaToken = !isChina ? await getRecaptchaToken(recaptchaVersion) : simpleCaptchaCode.value;

            if(!captchaToken){
                setCaptchaError(isChina ? getSimpleCaptchaValidationError() : getRecaptcha2Error());
                setIsLoading(false);
                return;
            }

            const utm = getUTMFromStorage();

            response = await createAccount(
                CreateAccountReferrer.CartCreateAccount,
                email.value,
                '', // password.value
                captchaToken,
                recaptchaVersion,

                // message gears -------
                getPageNodeGUID(),
                utm ? utm.firstSource : '',
                utm ? utm.firstMedium : '',
                utm ? utm.firstCampaign : '',
                utm ? utm.lastSource : '',
                utm ? utm.lastMedium : '',
                utm ? utm.lastCampaign : '',
            );
        }
        catch(ex){}

        // all is well ---------------
        if(
            response &&
            response.statusCode === 200 &&
            response.data &&
            response.data.isValid &&
            response.data.relatedData){

            await onCreateAccountSuccess(response);
            return;
        }

        // handle errors ---------
        setIsLoading(false);
        onCreateAccountError(response);
    };

    /**
     * and enter / return keys
     */
    const handleEnter = async (evt: KeyboardEvent<HTMLInputElement>) => {
        if(evt.key === 'Enter') {
            await performCreateAccount();
        }
    };

    /**
     * empty errors on click
     */
    const handleFieldClick = () =>{
        setResponse(null);
        setCaptchaError('');
    };

    /**
     * on 'log in now' button click
     */
    const switchToLoginHandler = () => {
        dispatch(
            rootActions.main({
                step2Tab: Step2Tabs.Login,
            })
        );
    };

    const prevent = (evt: MouseEvent) => {
      evt.preventDefault();
      evt.stopPropagation();
    };

    const externalLoginCallback = (response: { data: IServerValidation, statusCode: number } | null) => {
        if( response &&
            response.statusCode === 200 &&
            response.data &&
            response.data.isValid) {
            onCreateAccountSuccess(response);
        }
        else{
            // render errors
            onCreateAccountError(response);
        }
    };

    const googleSignedIn = useCallback(async (evt: CustomEvent) => {
        if(!evt || !evt.detail) return;
        await onCreateAccountSuccess(evt.detail);
    }, [ onCreateAccountSuccess ]);

    const googleError = useCallback((evt: CustomEvent) => {
        if(!evt || !evt.detail) return;
        onCreateAccountError(evt.detail);
    }, [ onCreateAccountError ]);

    useEffect(() => {
        // @ts-ignore
        document.body.addEventListener(GOOGLE_ERROR, googleError);
        // @ts-ignore
        document.body.addEventListener(GOOGLE_SIGNED_IN, googleSignedIn);

        return () => {
            // @ts-ignore
            document.body.removeEventListener(GOOGLE_ERROR, googleError);
            // @ts-ignore
            document.body.removeEventListener(GOOGLE_SIGNED_IN, googleSignedIn);
        };
    }, [googleError, googleSignedIn]);

    return (
        <div
            style={{
                width: '350px',
                maxWidth: '100%'
            }}>

            <ExternalLogin
                facebookText="Sign up with Facebook"
                googleTextParam="signup_with"
                dividerText={ shouldDisableCheckout ? 'Or' : 'Or, Create Account' }
                isShoppingCart={ true }
                callback={ externalLoginCallback }
                localData={ getLocalData() }
            />

            {
                shouldDisableCheckout &&
                <>
                    <a
                        className="w-btn"
                        href="/create-account">Create Account</a>

                    {
                        /* ------------- google recaptcha version 2 or 3 ------------- */
                        !isChina &&
                        <div id="recaptcha"
                             className={`mb-4 flex justify-center recaptcha recaptcha-version-${recaptchaVersion}`}/>
                    }

                    {
                        /* ------------- china simple captcha ------------- */
                        isChina &&
                        <ChinaCaptchaField
                            control={simpleCaptchaCode}
                            setSimpleCaptchaCode={setSimpleCaptchaCode}
                            simpleCaptchaAttempts={simpleCaptchaAttempts}
                        />
                    }

                    {
                        captchaError &&
                        <div className="text-sm text-red my-4 text-center"
                             data-id={recaptcha2WidgetID}>{captchaError}</div>
                    }

                    <div onMouseDown={prevent}>
                        <FormTerms classes="flex justify-center text-center mt-4 text-sm"/>
                    </div>

                    {
                        !isChina && recaptchaVersion === 3 &&
                        <div className="mt-8 text-center">
                            <Recaptcha3Text/>
                        </div>
                    }
                </>
            }

            {
                !shouldDisableCheckout &&
                <>
                    <FormTextField
                        id={ 'email-tb' }
                        label={ 'Email' }
                        control={ email }
                        maxLength={ 80 }
                        onChange={ evt => {
                            const $target = evt.target as HTMLInputElement;
                            setEmail(updateControl(email, $target.value, !email.isValid));
                            setResponse(null); // empty server error
                            setCaptchaError('');
                        }}
                        onBlur={ () => {
                            setEmail(validateControl(email));
                        }}
                        onClick={ handleFieldClick }
                        onKeyUp={ handleEnter }
                        onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                            setEmail(updateControl(email, removeInvisibleCharsOnPaste(evt)));
                        }}
                    />

                    <div className="text-sm text-gray-500">
                        <div className="flex">
                            <span className="font-bold mr-2">Make sure your email is correct</span>
                            <Tooltip
                                width="200px"
                                $button={
                                    <button
                                        type="button"
                                        className="text-blue-200">
                                        Learn why
                                    </button>
                                }>
                                We'll use the info you provide to create an account and register your products. An email will be sent to create your password.
                            </Tooltip>
                        </div>

                        <div className="mb-4">
                            <span className="mr-2">Already have an account?</span>
                            <button
                                type="button"
                                className="text-blue-200"
                                onClick={ switchToLoginHandler }>
                                Log in now
                            </button>
                        </div>
                    </div>

                    {
                        /* ------------- google recaptcha version 2 or 3 ------------- */
                        !isChina &&
                        <div id="recaptcha" className={`mb-4 flex justify-center recaptcha recaptcha-version-${ recaptchaVersion }`}/>
                    }

                    {
                        /* ------------- china simple captcha ------------- */
                        isChina &&
                        <ChinaCaptchaField
                            control={ simpleCaptchaCode }
                            setSimpleCaptchaCode={ setSimpleCaptchaCode }
                            simpleCaptchaAttempts={ simpleCaptchaAttempts }
                        />
                    }

                    <div className="mb-2">

                        { isLoading && <FormPreloader /> }

                        {
                            !isLoading &&
                            <button
                                className="w-btn"
                                type="button"
                                onClick={async () => {
                                    await performCreateAccount();
                                }}
                            >Submit</button>
                        }
                    </div>

                    {
                        response && !response.isValid && (
                            <div className="text-sm text-red my-4 text-center">
                                <ValidationErrors errors={ response.errors } />
                            </div>
                        )
                    }

                    {
                        captchaError &&
                        <div className="text-sm text-red my-4 text-center" data-id={ recaptcha2WidgetID }>{ captchaError }</div>
                    }

                    <div onMouseDown={ prevent }>
                        <FormTerms classes="flex justify-center text-center mt-4 text-sm" />
                    </div>

                    {
                        !isChina && recaptchaVersion === 3 &&
                        <div className="mt-8 text-center">
                            <Recaptcha3Text />
                        </div>
                    }

                </>
            }
        </div>
    )
};

export default  CartCreateAccount;